import { Link } from "react-router-dom";
import { ChevronLeft, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

const LAST_UPDATED = "May 2026";

// Structured content. Keep it data-driven so we can easily edit clauses without
// hand-juggling JSX, and so the auto-generated table-of-contents stays in sync.
const SECTIONS = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    body: [
      "Account details — your name, email address, password (stored as a secure hash), and country. We also store a one-time verification code temporarily for email confirmation.",
      "Reading and engagement activity — devotionals you open, sections you bookmark or highlight, comments you post, likes you give, and shares you initiate.",
      "Subscription & payment data — payment is processed by Paystack. We never see or store your card details. We retain the resulting subscription status, renewal date, and a transaction reference.",
      "Device & technical data — app version, operating system, generic device model, language preference, and approximate diagnostic logs to keep the App working reliably.",
      "Optional content — notes, reflections, or images you create within the App are stored only when you explicitly save them.",
    ],
    list: true,
  },
  {
    id: "how-we-use",
    title: "2. How We Use Your Information",
    body: [
      "Provide and personalize the daily devotional and Bible study experience.",
      "Authenticate your account and keep it secure.",
      "Process subscriptions and manage premium access via our payment partner.",
      "Send transactional emails (verification codes, password resets, receipts) and — only with your consent — devotional reminders or product updates.",
      "Improve the App, fix bugs, and understand which features are most helpful in aggregate, non-identifying form.",
      "Detect and prevent abuse, spam, or violation of our Terms of Service.",
    ],
    list: true,
  },
  {
    id: "sharing",
    title: "3. Sharing Your Information",
    intro:
      "We do not sell your personal data. We share it only with the limited categories below:",
    body: [
      "Service providers who help us run the App — for example, our hosting provider, email delivery service, and Paystack for payments. They process data only on our behalf and under contractual confidentiality.",
      "Other community members — when you post a comment or a reply, your display name and the comment text are visible to other users of the App.",
      "Legal & safety — when required to comply with applicable law, respond to lawful requests, or protect the rights, safety, and property of users, third parties, or the App.",
    ],
    list: true,
  },
  {
    id: "sharing-devotionals",
    title: "4. Sharing Devotionals & Scripture",
    paragraphs: [
      "When you share a devotional or daily scripture from the App, the native share sheet generates a link to the devotional or verse on our website. We do not access the recipient's contact information; the share is handled by your device's operating system.",
    ],
  },
  {
    id: "retention",
    title: "5. Data Retention",
    paragraphs: [
      "We retain your information while your account is active. If you delete your account, we delete or anonymize personal data within 30 days, except where retention is required for legitimate business or legal reasons (such as financial records or fraud prevention).",
    ],
  },
  {
    id: "your-rights",
    title: "6. Your Rights & Choices",
    body: [
      "Access & correction — view and edit your profile from the Profile tab, or use Settings → Download My Data to get a full JSON export of everything tied to your account.",
      "Notifications — turn devotional reminders on or off from the system Settings for The Greater Life.",
      "Account deletion — open Settings → Delete Account in the mobile app to permanently remove your account, or email us from your registered address at hello@thegreaterlife.org.",
      "Withdraw consent — you may withdraw any consent previously given; this won't affect processing carried out before the withdrawal.",
    ],
    list: true,
  },
  {
    id: "security",
    title: "7. Security",
    paragraphs: [
      "We use industry-standard safeguards including HTTPS in transit, secure password hashing, access controls, and least-privilege storage. No system is perfectly secure, but we work continually to protect your data.",
    ],
  },
  {
    id: "children",
    title: "8. Children",
    paragraphs: [
      "The Greater Life is intended for users aged 13 and over. We do not knowingly collect information from children under 13. If you believe a child has provided us with personal information, please contact us so we can delete it.",
    ],
  },
  {
    id: "international",
    title: "9. International Users",
    paragraphs: [
      "Your information may be processed in countries other than your own, including for hosting and payment processing. We take appropriate measures to protect your data in transfer.",
    ],
  },
  {
    id: "changes",
    title: "10. Changes to this Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time. We will notify you of material changes by updating the \"Last updated\" date and, where appropriate, through an in-app notice or email.",
    ],
  },
  {
    id: "contact",
    title: "11. Contact Us",
    paragraphs: [
      "For questions about this policy or your data, email us at hello@thegreaterlife.org or visit thegreaterlife.org.",
    ],
  },
];

function Section({ section }) {
  const { id, title, body, paragraphs, intro, list } = section;
  return (
    <section id={id} className="scroll-mt-24 space-y-3">
      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">{title}</h2>
      {intro && <p className="text-muted-foreground">{intro}</p>}
      {paragraphs?.map((p, i) => (
        <p key={i} className="text-muted-foreground leading-relaxed">
          {p}
        </p>
      ))}
      {list && Array.isArray(body) && (
        <ul className="space-y-2 pl-5 list-disc text-muted-foreground marker:text-primary">
          {body.map((b, i) => (
            <li key={i} className="leading-relaxed">
              {b}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <div className="min-h-dvh bg-background">
      {/* Slim header — works whether or not the user is authenticated */}
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="privacy-home-link"
            className="inline-flex items-center gap-2 text-sm font-semibold tracking-tight"
          >
            <span className="size-8 rounded-md bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center">
              <BookOpen className="size-4" />
            </span>
            The Greater Life
          </Link>
          <Button
            asChild
            variant="ghost"
            size="sm"
            data-testid="privacy-back-button"
          >
            <Link to="/">
              <ChevronLeft className="size-4 mr-1" />
              Back
            </Link>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
        {/* Title block */}
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Last updated · {LAST_UPDATED}
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">
            The Greater Life (the "App") is operated to help you grow spiritually
            through daily devotionals, Bible reading and study, and community
            discussion. We value your trust and are committed to protecting your
            personal information. This policy explains what we collect, why we
            collect it, and the choices you have.
          </p>
        </div>

        {/* Table of contents */}
        <nav
          aria-label="Sections"
          data-testid="privacy-toc"
          className="mb-12 rounded-xl border border-border bg-muted/30 p-5"
        >
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            Contents
          </p>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-foreground hover:text-primary transition-colors"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Body */}
        <article className="space-y-10">
          {SECTIONS.map((s) => (
            <Section key={s.id} section={s} />
          ))}
        </article>

        <div className="mt-14 pt-6 border-t border-border">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} The Greater Life. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
}
